import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React from 'react';
import { findIndex } from 'lodash';

import Icon from '~/components/base/Icon';

import BaseLink from '~/components/base/BaseLink';

import { transition } from '~/styles/transition';
import { Hiddenh2 } from '~/styles/helpers';
import { getSectionSpacing, setSmallSpacing } from '~/utilities/spacing';

import { formatText } from '~/utilities/helpers';

const OuterWrapper = styled.nav`
    ${setSmallSpacing('padding-top')};
    ${({ marginBottom }) => getSectionSpacing(marginBottom, 'margin-bottom')};

    @media (max-width: ${({ theme }) => theme.breakpoint.largePhoneMax}) {
        display: none;
    }
`;

const Wrapper = styled.ul`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: 1.3rem;
    margin: -10px -7.5px 0;
`;

const ArchiveIcon = styled(Icon)`
    margin-right: 15px;
`;

const Link = styled(BaseLink)`
    ${transition({}, 'color')};
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.text.blue};
    font-size: 1.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.accent.gray};
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        font-size: 1.7rem;
    }
`;

const Span = styled.span`
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.text.blue};
    font-size: 1.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        font-size: 1.7rem;
    }
`;

const Button = styled.button`
    appearance: none;
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.text.blue};
    font-size: 1.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        font-size: 1.7rem;
    }
`;

const Future = styled.span`
    color: ${({ theme }) => theme.colors.accent.gray};
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    opacity: 0.5;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        font-size: 1.7rem;
    }
`;

const BreadcrumbWrapper = styled.li`
    padding: 10px 7.5px 0;
    display: flex;
    align-items: center;

    &:not(:first-of-type) {
        ${Link} {
            color: ${({ theme }) => theme.colors.accent.lightGray};
            font-weight: ${({ theme }) => theme.fontWeights.bold};

            &:hover,
            &:focus {
                color: ${({ theme }) => theme.colors.accent.gray};
            }
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
`;

const Chevron = styled(Icon)`
    margin-left: 15px;
`;

const Last = styled.span`
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.accent.gray};
    font-size: 1.4rem;
    font-weight: ${({ theme }) => theme.fontWeights.bold};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        font-size: 1.7rem;
    }
`;

const Breadcrumbs = ({
    crumbs,
    marginBottom = 'medium',
    icon = '',
    useProgress = false,
    className = ''
}) => {
    const currentIndex = findIndex(crumbs, { current: true });
    return (
        <OuterWrapper {...{ marginBottom, className }} aria-label="Breadcrumb">
            <Hiddenh2 id="breadcrumbNavLabel">Breadcrumb Navigation</Hiddenh2>
            <Wrapper aria-labelledby="breadcrumbNavLabel">
                {crumbs &&
                    crumbs.map(
                        ({ crumbLabel, pathname, current, onClick }, index) => (
                            <Container key={crumbLabel}>
                                {index === 0 && icon && (
                                    <ArchiveIcon
                                        {...{ icon }}
                                        color="textBlue"
                                        size={24}
                                        sizeLarge={24}
                                    />
                                )}
                                {useProgress ? (
                                    current ? (
                                        <BreadcrumbWrapper aria-current="step">
                                            <Last>
                                                {formatText(crumbLabel)}
                                            </Last>
                                            {index < crumbs.length - 1 && (
                                                <Chevron
                                                    icon="breadcrumb-chevron"
                                                    color="gray"
                                                    size={12}
                                                />
                                            )}
                                        </BreadcrumbWrapper>
                                    ) : index < currentIndex ? (
                                        <BreadcrumbWrapper>
                                            {pathname ? (
                                                <Link to={pathname}>
                                                    {formatText(crumbLabel)}
                                                </Link>
                                            ) : onClick ? (
                                                <Button onClick={onClick}>
                                                    {formatText(crumbLabel)}
                                                </Button>
                                            ) : (
                                                <Span>
                                                    {formatText(crumbLabel)}
                                                </Span>
                                            )}
                                            {index < crumbs.length - 1 && (
                                                <Chevron
                                                    icon="breadcrumb-chevron"
                                                    color="gray"
                                                    size={12}
                                                />
                                            )}
                                        </BreadcrumbWrapper>
                                    ) : (
                                        <BreadcrumbWrapper>
                                            <Future>
                                                {formatText(crumbLabel)}
                                            </Future>
                                            {index < crumbs.length - 1 && (
                                                <Chevron
                                                    icon="breadcrumb-chevron"
                                                    color="gray"
                                                    size={12}
                                                />
                                            )}
                                        </BreadcrumbWrapper>
                                    )
                                ) : index < crumbs.length - 1 ? (
                                    <BreadcrumbWrapper>
                                        {pathname ? (
                                            <Link to={pathname}>
                                                {formatText(crumbLabel)}
                                            </Link>
                                        ) : (
                                            <Span>
                                                {formatText(crumbLabel)}
                                            </Span>
                                        )}
                                        <Chevron
                                            icon="breadcrumb-chevron"
                                            color="gray"
                                            size={12}
                                        />
                                    </BreadcrumbWrapper>
                                ) : (
                                    <BreadcrumbWrapper aria-current="page">
                                        <Last>
                                            {formatText(
                                                crumbs[crumbs.length - 1]
                                                    .crumbLabel
                                            )}
                                        </Last>
                                    </BreadcrumbWrapper>
                                )}
                            </Container>
                        )
                    )}
            </Wrapper>
        </OuterWrapper>
    );
};

Breadcrumbs.propTypes = {
    crumbs: PropTypes.arrayOf(
        PropTypes.shape({
            crumbLabel: PropTypes.string.isRequired,
            pathname: PropTypes.string,
            current: PropTypes.bool,
            onClick: PropTypes.func
        }).isRequired
    ).isRequired,
    marginBottom: PropTypes.oneOf(['none', 'medium', 'default']),
    icon: PropTypes.string,
    useProgress: PropTypes.bool,
    className: PropTypes.string
};

export default Breadcrumbs;
